import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashWithout from 'lodash/fp/without';
import lodashUniq from 'lodash/fp/uniq';
import queryString from 'query-string';
import typeCheck from 'common/util/typeCheck';

import { UTILITY_USER_ROLE_ADMIN } from 'common/utility/userRoles';

import { UTILITY_PROVIDERS_MILES_RANGE } from 'common/utility/constants';

import {
  SR_API_ACTION_UTILITY_ACTIVATION_GET,
  SR_API_ACTION_UTILITY_ACTIVATION_UPDATE,
  SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
  SR_API_ACTION_UTILITY_PROVIDER_UPSERT,
  SR_API_ACTION_UTILITY_PROVIDER_UPDATE_ADDRESS_COVERAGE,
  SR_API_ACTION_UTILITY_SERVICE_AREA_PROVIDER_MAPPING,
  SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES,
  SR_API_ACTION_ADDRESS_FCC_CONFIRM_MAPPING,
} from 'api/utilityActionTypes';

import { SCREEN_ID_UTILITY_PROFIT_ADMIN_PROCESSING_ACTIVATION } from 'client/constants/screenIds';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import { withUtilityApiState } from 'client/helpers/withApiState';
import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';

import branch from 'client/core/branch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityActivationActions from 'client/actions/UtilityActivation';
import UtilityProviderActions from 'client/actions/UtilityProvider';
import UtilityDealerActions from 'client/actions/UtilityDealer';
import UtilityUserActions from 'client/actions/UtilityUser';
import { getRangeIdentifier } from 'client/actions/UtilityProvider/getMappedProvidersInRange';

import SrUtilityProfitAdminProcessActivationPage from 'client/components/SrUtilityProfitAdminProcessActivationPage/SrUtilityProfitAdminProcessActivationPage';
import UPProviderSearchForm from 'client/components/UPProviderSearchForm/UPProviderSearchForm';
import UPProviderUpsertForm from 'client/components/UPProviderUpsertForm/UPProviderUpsertForm';

@withTransitionHook
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ADMIN],
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_UTILITY_PROFIT_ADMIN_PROCESSING_ACTIVATION)
@withScrollToTop
@withUtilityApiState({
  getUtilityActivationApiState: {
    action: SR_API_ACTION_UTILITY_ACTIVATION_GET,
  },
})
@branch(({ match }) => ({
  currentUser: ['utilityUser'],
  utilityActivation: ['utilityActivations', 'byShortId', match.params.utilityActivationShortId],
  utilityProvidersList: ['utilityProviders', 'list'],
  utilityDealers: ['utilityDealers', 'list'],
  utilitySalesReps: ['utilitySalesReps', 'list'],
}))
@branch(({ utilityActivation }) => {
  if (!utilityActivation) {
    return {};
  }

  const result = {};

  // Get utility types based on partnerId
  const partnerId = utilityActivation.utilityPartnerId;
  result.utilityTypes = partnerId
    ? ['utilityActivations', 'typesByPartnerId', partnerId]
    : ['utilityActivations', 'types'];

  if (utilityActivation.address) {
    const rangeIdentifier = getRangeIdentifier({
      latitude: utilityActivation.address.latitude,
      longitude: utilityActivation.address.longitude,
      rangeInMiles: UTILITY_PROVIDERS_MILES_RANGE,
    });
    result.utilityProvidersInRange = ['utilityProviders', 'coverageByRange', rangeIdentifier];
    result.utilityProvidersByAddress = [
      'utilityProviders',
      'byAddressId',
      utilityActivation.address.id,
    ];
  }

  return result;
})
class UtilityProfitAdminProcessingActivationPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Utility Profit Admin | Process Activation',
  });

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    utilityActivation: PropTypes.object,
    utilityProvidersList: PropTypes.array,
    utilityDealers: PropTypes.array,
    utilityTypes: PropTypes.array,
    utilityProvidersInRange: PropTypes.array,
    getUtilityActivationApiState: PropTypes.object.isRequired,
  };

  static fetchData(match, inject) {
    return Promise.all([
      UtilityProviderActions.getList(inject),
      UtilityDealerActions.getList(inject),
      UtilityUserActions.getSalesRepList(inject),
      UtilityActivationActions.getByShortId(inject, {
        utilityActivationShortId: match.params.utilityActivationShortId,
      }).then((utilityActivation) => {
        const promises = [];

        // Fetch types with partnerId from the activation
        const partnerId = utilityActivation?.utilityPartnerId;
        promises.push(UtilityActivationActions.getTypesList(inject, { partnerId }));

        if (utilityActivation?.address) {
          promises.push(
            UtilityProviderActions.getMappedProvidersInRange(inject, {
              latitude: utilityActivation.address.latitude,
              longitude: utilityActivation.address.longitude,
              rangeInMiles: UTILITY_PROVIDERS_MILES_RANGE,
            }),
          );
          promises.push(
            UtilityProviderActions.getListByAddressId(inject, {
              addressId: utilityActivation.address.id,
            }),
          );
        }

        return Promise.all(promises);
      }),
    ]);
  }

  constructor(props) {
    super(props);
    this.state = {
      serviceAreaProviderMappingArgs: null,
      serviceAreaProviderMappingIsAdding: false,
      serviceAreaMappedProvidersByTypeId: {},
    };
  }

  appendServiceAreaMappedProvider = ({ utilityTypeId, utilityProvider }) => {
    if (!utilityTypeId || !utilityProvider?.id) {
      return;
    }

    this.setState((prevState) => {
      const existingProviders = prevState.serviceAreaMappedProvidersByTypeId[utilityTypeId] || [];
      const hasProvider = existingProviders.some(({ id }) => id === utilityProvider.id);

      if (hasProvider) {
        return null;
      }

      return {
        serviceAreaMappedProvidersByTypeId: {
          ...prevState.serviceAreaMappedProvidersByTypeId,
          [utilityTypeId]: [...existingProviders, utilityProvider],
        },
      };
    });
  };

  updateProviderCoveragePoints = ({
    addressId,
    utilityTypeId,
    utilityProviderIds,
    coveragePointsConfig,
    mappingNotes,
  }) => {
    typeCheck('addressId::PositiveNumber', addressId);
    typeCheck('utilityProviderIds::Array', utilityProviderIds);
    typeCheck('utilityTypeId::PositiveNumber', utilityTypeId);
    typeCheck('coveragePointsConfig::Maybe Array', coveragePointsConfig);
    typeCheck('mappingNotes::Maybe String');

    const { dispatch } = this.props;
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PROVIDER_UPDATE_ADDRESS_COVERAGE,
      payload: {
        addressId,
        utilityTypeId,
        utilityProviderIds,
        coveragePointsConfig,
        mappingNotes,
      },
      onError(error) {
        global.alert(error.message);
      },
    }).then(() => {
      return Promise.all([
        this.reloadUtilityActivation(),
        dispatch(UtilityProviderActions.getListByAddressId, {
          addressId,
          refresh: true,
        }),
      ]);
    });
  };

  updateActivation = (payload) => {
    const { dispatch } = this.props;

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE,
      payload,
      onError(error) {
        global.alert(error.message);
      },
    }).then(this.reloadUtilityActivation);
  };

  updateActivationItem = (payload) => {
    const { dispatch } = this.props;
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
      payload,
      onError(error) {
        global.alert(error.message);
      },
    }).then(this.reloadUtilityActivation);
  };

  toggleRequiredType = (utilityTypeId) => {
    const { utilityActivation, utilityTypes, dispatch } = this.props;

    const utilityType = utilityTypes.find(({ id }) => id === utilityTypeId);
    const requiredTypes = utilityActivation.requiredTypes.map(({ utilityTypeId }) => utilityTypeId);
    const isRemovingType = requiredTypes.includes(utilityTypeId);

    const newUtilityTypeIds = isRemovingType
      ? lodashWithout([utilityTypeId], requiredTypes)
      : lodashUniq([...requiredTypes, utilityTypeId]);

    const confirmMessage = `Are you sure you want to mark "${utilityType.label}" as ${
      isRemovingType ? 'not ' : ''
    }required?`;

    if (!global.confirm(confirmMessage)) {
      return;
    }

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES,
      payload: {
        addressId: utilityActivation.addressId,
        utilityTypeIds: newUtilityTypeIds,
      },
      onError(error) {
        global.alert(error.message);
      },
    }).then(this.reloadUtilityActivation);
  };

  onMapServiceAreaProvider = ({ utilityServiceAreaId, utilityTypeId, companyName, addressId }) => {
    this.setState({
      serviceAreaProviderMappingArgs: {
        utilityServiceAreaId,
        utilityTypeId,
        companyName,
        addressId,
      },
      serviceAreaProviderMappingIsAdding: false,
    });
  };

  onMapServiceAreaProviderSelected = (utilityProvider) => {
    const { dispatch } = this.props;
    const { serviceAreaProviderMappingArgs } = this.state;

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_SERVICE_AREA_PROVIDER_MAPPING,
      payload: {
        utilityServiceAreaId: serviceAreaProviderMappingArgs.utilityServiceAreaId,
        utilityProviderId: utilityProvider.id,
      },
      onSuccess: () => {
        this.appendServiceAreaMappedProvider({
          utilityTypeId: serviceAreaProviderMappingArgs.utilityTypeId,
          utilityProvider,
        });
        this.reloadUtilityActivation();
        this.onCloseMapServiceAreaProvider();
      },
    });
  };

  onMapServiceAreaProviderAddNew = () => {
    this.setState({
      serviceAreaProviderMappingIsAdding: true,
    });
  };

  onMapServiceAreaProviderAddNewSubmit = (utilityProviderUpsertForm) => {
    const { dispatch } = this.props;
    const { serviceAreaProviderMappingArgs } = this.state;

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PROVIDER_UPSERT,
      payload: utilityProviderUpsertForm,
    }).then(({ utilityProvider }) => {
      return dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_SERVICE_AREA_PROVIDER_MAPPING,
        payload: {
          utilityServiceAreaId: serviceAreaProviderMappingArgs.utilityServiceAreaId,
          utilityProviderId: utilityProvider.id,
        },
        onSuccess: () => {
          this.appendServiceAreaMappedProvider({
            utilityTypeId: serviceAreaProviderMappingArgs.utilityTypeId,
            utilityProvider,
          });
          this.reloadUtilityActivation();
          this.onCloseMapServiceAreaProvider();
        },
      });
    });
  };

  onCloseMapServiceAreaProvider = () => {
    this.setState({
      serviceAreaProviderMappingArgs: null,
      serviceAreaProviderMappingIsAdding: false,
    });
  };

  reloadUtilityActivation = () => {
    const { dispatch, match } = this.props;
    return Promise.all([
      dispatch(UtilityProviderActions.getList, {
        refresh: true,
      }),
      dispatch(UtilityActivationActions.getByShortId, {
        utilityActivationShortId: match.params.utilityActivationShortId,
        forceRefresh: true,
      }).then((utilityActivation) => {
        if (utilityActivation?.address) {
          dispatch(UtilityProviderActions.getMappedProvidersInRange, {
            latitude: utilityActivation.address.latitude,
            longitude: utilityActivation.address.longitude,
            rangeInMiles: UTILITY_PROVIDERS_MILES_RANGE,
            utilityTypeId: null,
          });
        }
      }),
    ]);
  };

  onConfirmMapping = ({ addressId, utilityTypeId }) => {
    const { dispatch } = this.props;

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_FCC_CONFIRM_MAPPING,
      payload: {
        addressId,
        utilityTypeId,
      },
      onSuccess: () => {
        this.reloadUtilityActivation();
      },
    });
  };

  render() {
    const {
      currentUser,
      utilityActivation,
      utilityProvidersList,
      utilityProvidersInRange,
      utilityProvidersByAddress,
      utilityTypes,
      utilityDealers,
      utilitySalesReps,
      getUtilityActivationApiState,
      isTransitioning,
      match,
      location,
    } = this.props;

    const queryParams = queryString.parse(location.search || '');

    const {
      serviceAreaProviderMappingArgs,
      serviceAreaProviderMappingIsAdding,
      serviceAreaMappedProvidersByTypeId,
    } = this.state;

    if (
      isTransitioning ||
      !utilityProvidersList ||
      !utilityTypes ||
      !utilityDealers ||
      !utilitySalesReps ||
      (getUtilityActivationApiState.inProgress && !utilityActivation)
    ) {
      return <div className="loading-ellipses" />;
    }

    if (!utilityActivation) {
      return (
        <div>
          {`Utility Activation with short ID ${match.params.utilityActivationShortId} not found.`}
        </div>
      );
    }

    return (
      <>
        <SrUtilityProfitAdminProcessActivationPage
          currentUser={currentUser}
          utilityProvidersList={utilityProvidersList}
          utilityProvidersInRange={utilityProvidersInRange}
          utilityProvidersByAddress={utilityProvidersByAddress}
          utilityTypes={utilityTypes}
          utilityDealers={utilityDealers}
          utilitySalesReps={utilitySalesReps}
          utilityActivation={utilityActivation}
          updateProviderCoveragePoints={this.updateProviderCoveragePoints}
          updateActivation={this.updateActivation}
          updateActivationItem={this.updateActivationItem}
          toggleRequiredType={this.toggleRequiredType}
          onMapServiceAreaProvider={this.onMapServiceAreaProvider}
          onConfirmMapping={this.onConfirmMapping}
          serviceAreaMappedProvidersByTypeId={serviceAreaMappedProvidersByTypeId}
          backTitle={queryParams?.backTitle || null}
          backUrl={queryParams?.backUrl || null}
        />
        {serviceAreaProviderMappingArgs && (
          <UPProviderSearchForm
            utilityType={utilityTypes.find(
              ({ id }) => serviceAreaProviderMappingArgs.utilityTypeId === id,
            )}
            addressDisplay={serviceAreaProviderMappingArgs.companyName}
            onProviderSelected={this.onMapServiceAreaProviderSelected}
            onClose={this.onCloseMapServiceAreaProvider}
            onAddNew={this.onMapServiceAreaProviderAddNew}
          />
        )}
        {serviceAreaProviderMappingIsAdding && (
          <UPProviderUpsertForm
            title="Connect to a Provider"
            utilityType={utilityTypes.find(
              ({ id }) => serviceAreaProviderMappingArgs.utilityTypeId === id,
            )}
            initialValues={{
              addressId: serviceAreaProviderMappingArgs.addressId,
              utilityTypeId: serviceAreaProviderMappingArgs.utilityTypeId,
            }}
            addressDisplay={serviceAreaProviderMappingArgs.companyName}
            onCancel={this.onCloseMapServiceAreaProvider}
            onSubmit={this.onMapServiceAreaProviderAddNewSubmit}
          />
        )}
      </>
    );
  }
}

UtilityProfitAdminProcessingActivationPage.displayName =
  'UtilityProfitAdminProcessingActivationPage';

export default UtilityProfitAdminProcessingActivationPage;
